import { useRef, useEffect, useState } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Anchor, Zap } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { t } from '@/i18n/translations';

export const RewardModelSection = () => {
  const sectionRef = useRef(null);
  const [progress, setProgress] = useState(0); // 0–1 tied to scroll

  useEffect(() => {
    gsap.registerPlugin(ScrollTrigger);
    const st = ScrollTrigger.create({
      trigger: sectionRef.current,
      start: 'top 75%',
      end: 'top 15%',
      scrub: 0.3,
      onUpdate: (self) => setProgress(self.progress),
    });
    return () => st.kill();
  }, []);

  const { lang } = useLanguage();
  const tx = t(lang).reward;
  // Bar fills 0→80% in first 67% of scroll, then 80→100% in last 33%
  const barFill   = progress < 0.67 ? (progress / 0.67) * 80 : 80 + ((progress - 0.67) / 0.33) * 20;
  const base80    = Math.min(barFill, 80);
  const bonus20   = Math.max(0, barFill - 80);
  const counter1  = Math.round((base80 / 80) * 1400);
  const counter2  = Math.round((bonus20 / 20) * 20);

  return (
    <section
      id="reward-model"
      ref={sectionRef}
      data-testid="reward-model-section"
      className="scroll-section relative min-h-screen flex items-center overflow-hidden"
    >
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[300px] rounded-full bg-[#C87533]/5 blur-[100px]" />
      </div>

      <div className="section-content max-w-[900px] mx-auto px-4 sm:px-6 lg:px-8">

        {/* Header */}
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 mb-4 px-4 py-1.5 rounded-full border border-[#C87533]/30 bg-[#C87533]/10">
            <span className="font-mono text-xs text-[#C87533] uppercase tracking-wider">{tx.label}</span>
          </div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-3">
            {tx.headline} <span className="text-copper-gradient">{tx.headlineAccent}</span>
          </h2>
          <p className="text-white/50 text-base lg:text-lg">{tx.sub}</p>
        </div>

        {/* Progress Bar – scroll-driven */}
        <div className="mb-4">
          <div className="relative h-14 rounded-2xl overflow-hidden bg-white/5 border border-white/8">
            {/* 80% base */}
            <div
              className="absolute left-0 top-0 h-full flex items-center justify-center"
              style={{
                width: `${base80}%`,
                background: 'linear-gradient(90deg, #1a3a5c, #8B5E3C)',
              }}
            >
              {base80 > 35 && (
                <span className="font-display font-bold text-white text-lg select-none">
                  {Math.round(base80)}%
                </span>
              )}
            </div>
            {/* 20% bonus */}
            {bonus20 > 0 && (
              <div
                className="absolute top-0 h-full flex items-center justify-center"
                style={{
                  left: `${base80}%`,
                  width: `${bonus20}%`,
                  background: 'linear-gradient(90deg, #C87533, #FF8C2A)',
                  boxShadow: '0 0 24px rgba(255,140,42,0.5)',
                  animation: 'bonusPulse 2s ease-in-out infinite',
                }}
              >
                {bonus20 > 8 && (
                  <span className="font-display font-bold text-white text-base select-none">
                    {Math.round(bonus20)}%
                  </span>
                )}
              </div>
            )}
            {/* Divider at 80% */}
            {base80 >= 79 && (
              <div className="absolute top-0 h-full w-px bg-white/40" style={{ left: '80%' }} />
            )}
          </div>
          {progress < 0.05 && (
            <p className="text-center font-mono text-xs text-white/25 mt-3 uppercase tracking-wider animate-pulse">
              {tx.scrollHint}
            </p>
          )}
        </div>

        {/* Two columns */}
        <div className="grid grid-cols-2 gap-5 mb-10 mt-8">
          {/* 80% */}
          <div
            className="rounded-2xl border border-white/10 bg-white/3 p-6 lg:p-8"
            style={{ opacity: Math.min(base80 / 40, 1) }}
          >
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 rounded-xl bg-white/8 flex items-center justify-center">
                <Anchor className="w-5 h-5 text-white/60" />
              </div>
              <span className="font-mono text-xs text-white/40 uppercase tracking-wider">{tx.baseLabel}</span>
            </div>
            <div className="mb-2">
              <span className="font-display text-3xl lg:text-4xl font-bold text-white">
                {counter1.toLocaleString('sl-SI')} €
              </span>
              <span className="font-mono text-sm text-white/40 ml-2">neto+</span>
            </div>
            <p className="font-mono text-xs text-white/50 uppercase tracking-wider mb-4">{tx.baseFixed}</p>
            <p className="text-sm text-white/55 leading-relaxed">{tx.baseTagline}</p>
            <p className="text-xs text-white/30 mt-3 italic">{tx.baseNote}</p>
          </div>

          {/* 20% */}
          <div
            className="rounded-2xl border border-[#C87533]/25 p-6 lg:p-8 relative overflow-hidden"
            style={{
              background: 'linear-gradient(135deg, rgba(200,117,51,0.08), transparent)',
              opacity: Math.min(bonus20 / 10, 1),
            }}
          >
            <div className="relative">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 rounded-xl bg-[#C87533]/15 flex items-center justify-center">
                  <Zap className="w-5 h-5 text-[#C87533]" />
                </div>
                <span className="font-mono text-xs text-[#C87533]/70 uppercase tracking-wider">{tx.driveLabel}</span>
              </div>
              <div className="mb-2">
                <span className="font-display text-3xl lg:text-4xl font-bold text-[#C87533]">
                  {counter2} %
                </span>
              </div>
              <p className="font-mono text-xs text-[#C87533]/50 uppercase tracking-wider mb-4">{tx.driveFixed}</p>
              <p className="text-sm text-white/55 leading-relaxed">{tx.driveTagline}</p>
              <p className="text-xs text-white/30 mt-3 italic">{tx.driveNote}</p>
            </div>
          </div>
        </div>

        {/* Footer callout */}
        <div
          className="text-center rounded-2xl border border-white/8 bg-white/[0.02] py-4 px-6"
          style={{ opacity: Math.min(progress * 2, 1) }}
        >
          <p className="font-mono text-sm text-white/50 uppercase tracking-widest">
            {tx.footer}&nbsp;&nbsp;
            <span className="text-[#C87533]">{tx.footerAccent}</span>
          </p>
        </div>

      </div>
    </section>
  );
};
